import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaRocket, FaCheck, FaArrowRight } from 'react-icons/fa';

// Components
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import SchemaOrgLocalBusiness from '../components/SchemaOrgLocalBusiness';
import Portfolio from '../components/Portfolio';
import MagicPreview from '../components/MagicPreview';
import TechStack from '../components/TechStack';
import Performance from '../components/Performance';
import AboutMe from '../components/AboutMe';
import ClientLogos from '../components/ClientLogos';

// Design System
import {
  WebDevelopment,
  Maintenance,
  Cybersecurity,
  MobileApps,
  IconWrapper
} from '../design-system/icons';

// Data
import { services } from '../data/services';

const Home = () => {
  const [statsAnimated, setStatsAnimated] = useState(false);
  const phoneNumber = "+33689177385";

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Message envoyé avec succès! Nous vous répondrons sous 24h.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Une erreur est survenue. Veuillez réessayer.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Impossible de se connecter au serveur. Veuillez nous contacter par téléphone au 06 89 17 73 85.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Stats counter animation
  useEffect(() => {
    const animateStats = () => {
      const statsSection = document.querySelector('.stats-section');
      if (!statsSection) return;

      const rect = statsSection.getBoundingClientRect();

      if (!statsAnimated && rect.top < window.innerHeight && rect.bottom > 0) {
        setStatsAnimated(true);

        document.querySelectorAll('.stat-number').forEach(stat => {
          const target = parseInt(stat.getAttribute('data-target'));
          const isPercentage = stat.getAttribute('data-percentage') === 'true';
          const hasPlus = stat.getAttribute('data-plus') === 'true';

          let current = 0;
          const increment = target / 50;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              current = target;
              clearInterval(timer);
            }
            stat.textContent = Math.floor(current) +
              (isPercentage ? '%' : '') +
              (hasPlus ? '+' : '');
          }, 30);
        });
      }
    };

    window.addEventListener('scroll', animateStats);
    animateStats();
    return () => window.removeEventListener('scroll', animateStats);
  }, [statsAnimated]);

  // Get icon component based on service
  const getServiceIcon = (iconName, size = 32) => {
    const icons = {
      WebDevelopment: <WebDevelopment size={size} />,
      Maintenance: <Maintenance size={size} />,
      Cybersecurity: <Cybersecurity size={size} />,
      MobileApps: <MobileApps size={size} />,
    };
    return icons[iconName] || <WebDevelopment size={size} />;
  };

  return (
    <>
      <Helmet>
        <title>Olney Tech Solutions | Agence Web & Dépannage à Roncq & Phalempin</title>
        <meta name="description" content="Votre partenaire digital local à Roncq et Phalempin. Création de sites web modernes, dépannage informatique rapide et maintenance pour pros et particuliers." />
        <link rel="canonical" href="https://olney.dev/" />
      </Helmet>

      <SchemaOrgLocalBusiness />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20 relative overflow-hidden">
        {/* Ambient Glow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.2, scale: 1 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[var(--primary)] rounded-full blur-[120px]"
        />

        <div className="max-w-6xl mx-auto text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-block mb-6 px-6 py-2 rounded-full border border-[var(--secondary)] bg-[rgba(0,198,255,0.1)] backdrop-blur-md"
          >
            <span className="text-[var(--secondary)] font-bold tracking-[0.2em] text-sm uppercase">Agence Web Premium • Lille & Métropole</span>
          </motion.div>

          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-bold mb-8 font-['Space_Grotesk'] leading-tight">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="glitch-wrapper mb-2"
            >
              <span className="glitch-text text-white" data-text="Votre Partenaire Web & IT">Votre Partenaire Web & IT</span>
            </motion.div>
            <motion.span
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-gradient-electric"
            >
              à Phalempin & Roncq
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto font-light leading-relaxed"
          >
            Je transforme votre présence en ligne avec une <span className="text-white font-semibold">stratégie digitale d'élite</span>.
            Design futuriste, performance maximale, résultats concrets pour les pros du Nord.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-col sm:flex-row justify-center gap-6"
          >
            <Link to="/contact" className="btn-magnetic group relative px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl overflow-hidden transition-all hover:scale-105 shadow-[0_0_40px_rgba(112,0,255,0.4)]">
              <span className="relative z-10 flex items-center gap-3 text-lg uppercase tracking-wider">
                Lancer le Projet <FaRocket className="group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#8A2BE2] to-[#FF00E5] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
            <Link to="/portfolio" className="group px-8 py-4 bg-transparent border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all hover:border-white/40 uppercase tracking-wider text-lg backdrop-blur-sm">
              Voir nos Réalisations
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-white/50"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-scroll"></div>
          </div>
        </motion.div>
      </section>

      {/* Client Logos & Trust */}
      <ClientLogos />

      {/* About Me Section */}
      <AboutMe />

      {/* Stats Section */}
      <section className="stats-section py-10 border-y border-[var(--border-glass)] bg-[rgba(255,255,255,0.02)] backdrop-blur-sm relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { target: 150, plus: true, label: 'Projets Réalisés', color: 'from-[var(--primary)] to-[var(--secondary)]' },
              { target: 98, percentage: true, label: 'Satisfaction Client', color: 'from-[var(--secondary)] to-[var(--accent)]' },
              { target: 5, plus: true, label: "Années d'Expérience", color: 'from-[var(--accent)] to-[var(--primary)]' },
              { target: 24, label: 'Support Réactif', suffix: '/7', color: 'from-[var(--primary)] to-[var(--secondary)]' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="text-center group"
              >
                <div
                  className={`stat-number mb-2 text-transparent bg-clip-text bg-gradient-to-r ${stat.color} group-hover:scale-110 transition-transform duration-300`}
                  data-target={stat.target}
                  data-plus={stat.plus ? 'true' : 'false'}
                  data-percentage={stat.percentage ? 'true' : 'false'}
                >
                  {stat.target}{stat.percentage ? '%' : ''}{stat.plus ? '+' : ''}{stat.suffix || ''}
                </div>
                <div className="text-gray-400 uppercase text-xs tracking-[0.2em] font-semibold">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[var(--secondary)] rounded-full blur-[150px] opacity-5 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[var(--primary)] rounded-full blur-[150px] opacity-5 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)] text-sm font-bold tracking-widest uppercase"
            >
              Nos Expertises
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl font-bold mb-6 font-['Space_Grotesk'] text-white"
            >
              Solutions Digitales <span className="text-gradient-primary">Sur-Mesure</span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className={`glass-panel rounded-3xl p-10 card-reveal hover:border-[var(--${service.color})] transition-colors duration-500 group`}
              >
                <IconWrapper size="md" variant={service.color} glow={true} className="mb-8 group-hover:scale-110 transition-transform duration-500">
                  {getServiceIcon(service.icon)}
                </IconWrapper>
                <h3 className="text-2xl font-bold text-white mb-4 font-['Space_Grotesk']">{service.title}</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-300 text-sm">
                      <FaCheck className={`text-[var(--${service.color})] mr-3`} /> {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/services/${service.slug}`}
                  className={`text-[var(--${service.color})] font-bold uppercase tracking-wider text-sm hover:text-white transition-colors flex items-center gap-2 group-hover:gap-3`}
                >
                  En savoir plus <FaArrowRight />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[var(--primary)] font-bold uppercase tracking-wider hover:text-white transition-colors"
            >
              Voir tous nos services <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Magic AI Preview */}
      <MagicPreview />

      {/* Tech Stack */}
      <TechStack />

      {/* Performance */}
      <Performance />

      {/* Portfolio */}
      <Portfolio />

      {/* Technologies Section */}
      <section id="technologies" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16 fade-in">
            <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--accent)] bg-[rgba(255,0,229,0.1)] text-[var(--accent)] text-sm font-bold tracking-widest uppercase">TECHNOLOGIES</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 font-['Space_Grotesk'] text-white">
              Stack Technologique <span className="text-gradient-electric">Avancée</span>
            </h2>
            <p className="text-xl text-gray-400">
              Performance, sécurité et scalabilité au cœur de chaque ligne de code.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-6">
            {[
              { icon: '⚛️', name: 'React' },
              { icon: '🟢', name: 'Node.js' },
              { icon: '🐍', name: 'Python' },
              { icon: '🐳', name: 'Docker' },
              { icon: '☁️', name: 'AWS' },
              { icon: '📊', name: 'MongoDB' },
              { icon: '🔷', name: 'TypeScript' },
              { icon: '🎨', name: 'Tailwind' }
            ].map((tech, idx) => (
              <div key={idx} className="glass-panel rounded-2xl p-6 text-center hover:border-[var(--primary)] transition-all duration-300 hover:transform hover:-translate-y-2 fade-in group">
                <div className="text-4xl mb-3 group-hover:scale-125 transition-transform duration-300">{tech.icon}</div>
                <div className="text-gray-300 font-semibold group-hover:text-white">{tech.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-32 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--secondary)] bg-[rgba(0,198,255,0.1)] text-[var(--secondary)] text-sm font-bold tracking-widest uppercase"
            >
              Investissement
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl font-bold mb-6 font-['Space_Grotesk'] text-white"
            >
              Des Tarifs <span className="text-gradient-secondary">Transparents</span>
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pack Lancement Facile */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-panel rounded-3xl p-8 card-reveal border-[var(--primary)] shadow-[0_0_50px_rgba(112,0,255,0.15)] relative transform md:-translate-y-4 flex flex-col group"
            >
              <div className="absolute top-0 right-0 bg-[var(--primary)] text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-2xl uppercase tracking-wider">
                Recommandé
              </div>
              <div className="text-[var(--primary)] font-bold mb-2 tracking-widest uppercase text-sm">PACK LANCEMENT FACILE</div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white font-['Space_Grotesk']">500€</span>
                <span className="text-gray-400 text-sm block mt-2">+ 150€/mois (Tout inclus)</span>
              </div>
              <p className="text-gray-400 mb-8 text-sm italic border-l-2 border-[var(--primary)] pl-4">
                "La solution idéale pour se lancer sans se ruiner. Site Web + Maintenance IT complète."
              </p>
              <ul className="space-y-4 mb-8 flex-grow">
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--primary)] mr-3 flex-shrink-0" /> Site Web Pro (5 pages)</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--primary)] mr-3 flex-shrink-0" /> Hébergement & Nom de Domaine</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--primary)] mr-3 flex-shrink-0" /> Maintenance Technique 24/7</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--primary)] mr-3 flex-shrink-0" /> Modifications Illimitées (Texte/Img)</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--primary)] mr-3 flex-shrink-0" /> Support Informatique Inclus</li>
              </ul>
              <Link to="/contact" className="w-full btn-magnetic py-4 rounded-xl font-bold text-white shadow-lg shadow-purple-500/20 text-center uppercase tracking-wider text-sm bg-[var(--primary)] hover:bg-[#5a00cc] transition-colors block">
                Je Me Lance
              </Link>
            </motion.div>

            {/* Pack E-commerce Pro */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-panel rounded-3xl p-8 card-reveal hover:border-[var(--secondary)] transition-all duration-500 flex flex-col group"
            >
              <div className="text-[var(--secondary)] font-bold mb-2 tracking-widest uppercase text-sm">PACK E-COMMERCE PRO</div>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white font-['Space_Grotesk']">3500€</span>
                <span className="text-gray-400 text-sm block mt-2">ou dès 119€/mois*</span>
              </div>
              <p className="text-gray-400 mb-8 text-sm italic border-l-2 border-[var(--secondary)] pl-4">
                "Transformez vos visiteurs en clients fidèles avec une boutique ultra-rapide."
              </p>
              <ul className="space-y-4 mb-8 flex-grow">
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--secondary)] mr-3 flex-shrink-0" /> Boutique Complète (Shopify/Woo)</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--secondary)] mr-3 flex-shrink-0" /> Jusqu'à 50 Produits</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--secondary)] mr-3 flex-shrink-0" /> Paiements Sécurisés (Stripe/PayPal)</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--secondary)] mr-3 flex-shrink-0" /> Formation Gestion Back-Office</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--secondary)] mr-3 flex-shrink-0" /> Optimisation Conversion (CRO)</li>
              </ul>
              <Link to="/contact" className="w-full py-4 rounded-xl border border-[var(--secondary)] text-[var(--secondary)] font-bold hover:bg-[var(--secondary)] hover:text-white transition-all text-center uppercase tracking-wider text-sm block">
                Démarrer ma Boutique
              </Link>
            </motion.div>

            {/* Pack Innovation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-panel rounded-3xl p-8 card-reveal hover:border-[var(--accent)] transition-all duration-500 flex flex-col group"
            >
              <div className="text-[var(--accent)] font-bold mb-2 tracking-widest uppercase text-sm">PACK INNOVATION</div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-white font-['Space_Grotesk']">Sur Devis</span>
                <span className="text-gray-400 text-sm block mt-2">Projets complexes & Apps</span>
              </div>
              <p className="text-gray-400 mb-8 text-sm italic border-l-2 border-[var(--accent)] pl-4">
                "Pour les visionnaires qui veulent une solution unique et scalable."
              </p>
              <ul className="space-y-4 mb-8 flex-grow">
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--accent)] mr-3 flex-shrink-0" /> Application Web / SaaS</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--accent)] mr-3 flex-shrink-0" /> Intégration API & IA</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--accent)] mr-3 flex-shrink-0" /> Espace Membre / Dashboard</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--accent)] mr-3 flex-shrink-0" /> Architecture Cloud Scalable</li>
                <li className="flex items-center text-gray-300 text-sm"><FaCheck className="text-[var(--accent)] mr-3 flex-shrink-0" /> Maintenance Évolutive</li>
              </ul>
              <Link to="/contact" className="w-full py-4 rounded-xl border border-[var(--accent)] text-[var(--accent)] font-bold hover:bg-[var(--accent)] hover:text-white transition-all text-center uppercase tracking-wider text-sm block">
                Parler de mon Projet
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-12 text-center"
          >
            <p className="text-gray-500 text-sm">
              *Facilités de paiement disponibles. TVA non applicable, art. 293 B du CGI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <div className="bg-[rgba(30,41,59,0.3)]">
        <Testimonials />
      </div>

      {/* FAQ */}
      <FAQ />

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-full h-[500px] bg-gradient-to-t from-[rgba(112,0,255,0.1)] to-transparent pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20 fade-in">
            <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--primary)] bg-[rgba(112,0,255,0.1)] text-[var(--primary)] text-sm font-bold tracking-widest uppercase">CONTACT</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 font-['Space_Grotesk'] text-white">
              Démarrons Votre <span className="text-gradient-electric">Transformation</span>
            </h2>
            <p className="text-xl text-gray-400">
              Discutons de vos besoins et trouvons la solution idéale ensemble
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold text-white mb-8 font-['Space_Grotesk']">
                Prêt à passer au <span className="text-gradient-electric">niveau supérieur</span> ?
              </h3>
              <p className="text-gray-400 mb-12 text-lg leading-relaxed">
                Discutons de votre projet. Que vous ayez besoin d'un site vitrine, d'une boutique en ligne ou d'une application complexe, nous avons la solution.
              </p>

              <div className="space-y-8">
                <a href={`tel:${phoneNumber}`} className="flex items-center group">
                  <div className="w-14 h-14 rounded-full bg-[rgba(112,0,255,0.1)] flex items-center justify-center text-[var(--primary)] group-hover:scale-110 group-hover:bg-[var(--primary)] group-hover:text-white transition-all duration-300 shadow-[0_0_20px_rgba(112,0,255,0.2)]">
                    <span className="text-2xl">📞</span>
                  </div>
                  <div className="ml-6">
                    <div className="text-gray-400 text-sm uppercase tracking-wider font-bold mb-1">Téléphone</div>
                    <div className="text-white text-xl font-bold group-hover:text-[var(--primary)] transition-colors">06 89 17 73 85</div>
                  </div>
                </a>

                <a href="mailto:contact@olney.dev" className="flex items-center group">
                  <div className="w-14 h-14 rounded-full bg-[rgba(0,198,255,0.1)] flex items-center justify-center text-[var(--secondary)] group-hover:scale-110 group-hover:bg-[var(--secondary)] group-hover:text-white transition-all duration-300 shadow-[0_0_20px_rgba(0,198,255,0.2)]">
                    <span className="text-2xl">✉️</span>
                  </div>
                  <div className="ml-6">
                    <div className="text-gray-400 text-sm uppercase tracking-wider font-bold mb-1">Email</div>
                    <div className="text-white text-xl font-bold group-hover:text-[var(--secondary)] transition-colors">contact@olney.dev</div>
                  </div>
                </a>

                <div className="flex items-center group">
                  <div className="w-14 h-14 rounded-full bg-[rgba(255,0,229,0.1)] flex items-center justify-center text-[var(--accent)] group-hover:scale-110 group-hover:bg-[var(--accent)] group-hover:text-white transition-all duration-300 shadow-[0_0_20px_rgba(255,0,229,0.2)]">
                    <span className="text-2xl">📍</span>
                  </div>
                  <div className="ml-6">
                    <div className="text-gray-400 text-sm uppercase tracking-wider font-bold mb-1">Localisation</div>
                    <div className="text-white text-xl font-bold group-hover:text-[var(--accent)] transition-colors">Roncq, Hauts-de-France</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.form
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="glass-panel rounded-3xl p-10 relative overflow-hidden border-[var(--primary)]/20"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--primary)] rounded-full blur-[80px] opacity-20 pointer-events-none"></div>

              <div className="space-y-6 relative z-10">
                <div>
                  <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">Nom Complet</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-premium w-full bg-[rgba(255,255,255,0.03)] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all"
                    placeholder="Votre nom"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">Email Professionnel</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-premium w-full bg-[rgba(255,255,255,0.03)] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all"
                    placeholder="votre@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">Type de Projet</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="input-premium w-full bg-[rgba(255,255,255,0.03)] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all appearance-none"
                  >
                    <option value="Site Vitrine" className="bg-[#030014]">Site Vitrine</option>
                    <option value="E-commerce" className="bg-[#030014]">E-commerce</option>
                    <option value="Application Web" className="bg-[#030014]">Application Web / SaaS</option>
                    <option value="SEO / Audit" className="bg-[#030014]">SEO / Audit</option>
                    <option value="Autre" className="bg-[#030014]">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm font-bold mb-2 uppercase tracking-wider">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="input-premium w-full bg-[rgba(255,255,255,0.03)] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] outline-none transition-all resize-none"
                    placeholder="Décrivez votre projet..."
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full btn-magnetic py-4 rounded-xl font-bold text-lg shadow-lg shadow-purple-500/20 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  } bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-white hover:shadow-purple-500/40 transition-all transform hover:scale-[1.02] active:scale-[0.98]`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Transmission...
                    </span>
                  ) : (
                    'Envoyer le message'
                  )}
                </button>

                {submitStatus.type === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 text-center text-sm font-bold"
                  >
                    {submitStatus.message}
                  </motion.div>
                )}

                {submitStatus.type === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-4 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 text-center text-sm font-bold"
                  >
                    {submitStatus.message}
                  </motion.div>
                )}
              </div>
            </motion.form>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
